import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { StepProps, StepViewer } from 'components/Shared/StepViewer';
import { useForm } from 'components/UI/Form';
import { useOpenAccountAuth } from 'hooks/account/useOpenAccountAuth';
import { useHistory } from 'hooks/generic/useHistory';
import { useFireEvent } from 'hooks/useFireEvent';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import { useSearchParams } from 'hooks/useSearchParams';
import { RegisterCollectorComplete } from './RegisterCollectorComplete';
import { RegisterCompleteProvider } from '../../providers/RegisterCompleteProvider';
import { InfoBlockLoggedIn } from '../components/InfoBlockLoggedIn';
import { completeRegisterUser as completeRegisterUserRequest } from 'appRedux/restApi';
import { getCurrentLanguage } from 'utils/app/get-lang';
import { addQuery } from 'utils/generic/query-route';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import { StepViewerEvents } from 'constants/app/eventbus-events';
import { AccountType, RestAxiosError, RestBoolean } from 'interfaces';
import { IRegisterProps } from 'interfaces/Auth';

export const RegisterComplete = () => {
  const registerId = useId();
  const searchParams = useSearchParams();
  const { history } = useHistory();
  const { isLoggedIn, isLoginPending } = useIsLoggedIn();
  const { openAccountLogin } = useOpenAccountAuth();
  const [{ token, email }] = useState(() => {
    history.replace(addQuery({ token: undefined, email: undefined }));
    return searchParams;
  });
  const { t } = useTranslation();
  const { fireEvent } = useFireEvent();
  const [form] = useForm();
  const completeRegisterUserParams = (
    inputParams: Partial<IRegisterProps>
  ): ReturnType<typeof completeRegisterUserRequest> => {
    const _inputParams = { first_name: '', last_name: '', ...inputParams }; // first_name and last_name are required for backend
    _inputParams.account_type = AccountType.User;
    _inputParams.locale = getCurrentLanguage();
    _inputParams.token = token;
    _inputParams.email = email;
    _inputParams.only_validate_token = RestBoolean.False;
    return completeRegisterUserRequest(_inputParams);
  };

  const { refetch, error, isFetching, data } = useQuery<
    Awaited<ReturnType<typeof completeRegisterUserParams>>,
    RestAxiosError
  >({
    queryKey: [EAccountRouteKeys.ACCOUNT_REGISTER_COMPLETE],
    queryFn: () => completeRegisterUserParams(form.getFieldsValue(true)),
    enabled: false,
    retry: false
  });

  const onSubmitRegister = () => {
    form
      .validateFields()
      .then(() => void refetch())
      .catch(({ errorFields }) => {
        const firstErrorField = errorFields[0].name[0];
        form.scrollToField(firstErrorField, {
          behavior: 'smooth',
          scrollMode: 'if-needed',
          block: 'center'
        });
      });
  };

  useEffect(() => {
    if (!data) {
      return;
    }
    const isRedirecting = openAccountLogin();
    if (!isRedirecting) {
      return;
    }
    fireEvent({
      key: StepViewerEvents.SetNextLoading,
      id: registerId
    });
  }, [data]);

  useEffect(() => {
    if (error) {
      const _message = error?.response?.data?.message;
      _message && message.error(_message);
    }
  }, [error]);

  useEffect(() => {
    fireEvent({
      key: isFetching
        ? StepViewerEvents.SetNextLoading
        : StepViewerEvents.UnsetNextLoading,
      id: registerId
    });
  }, [registerId, isFetching]);

  if (!(token && email)) {
    return null;
  }

  if (isLoggedIn || isLoginPending) {
    return <InfoBlockLoggedIn loading={isLoginPending} />;
  }

  return (
    <RegisterCompleteProvider
      value={{ form: form, eventId: registerId, email: email }}
    >
      <StepViewer
        eventId={registerId}
        header={{
          showStepsProgress: false
        }}
        footer={{
          navigationButtonsFullWidth: true
        }}
        onFinish={onSubmitRegister}
        steps={[
          {
            title: {
              text: t('account.registerAsCollectorCompleteRegistration'),
              shortText: ''
            },
            subtitle: t(
              'account.registerAsCollectorCompleteRegistrationDescription'
            ),
            component: RegisterCollectorComplete,
            info: {
              show: false
            },
            nextBtnText: t('account.submit'),
            loaderSize: 'large' as StepProps['loaderSize']
          }
        ]}
      />
    </RegisterCompleteProvider>
  );
};
